import React, { useEffect, useMemo, useState } from 'react';
import { Box, Typography, Chip, alpha, useTheme, useMediaQuery } from '@mui/material';
import FormatQuoteRoundedIcon from '@mui/icons-material/FormatQuoteRounded';
import StarRoundedIcon from '@mui/icons-material/StarRounded';
import StarHalfRoundedIcon from '@mui/icons-material/StarHalfRounded';
import { filterTestimonials } from '../data/testimonials';

const ACCENT = '#137274';

const RatingStars = ({ rating = 5 }) => {
  const value = Math.min(5, Math.max(0, Number(rating) || 0));
  const fullStars = Math.floor(value);
  const hasHalf = value - fullStars >= 0.5;
  const emptyStars = 5 - fullStars - (hasHalf ? 1 : 0);

  return (
    <Box sx={{ display: 'flex', gap: 0.15, alignItems: 'center' }}>
      {Array.from({ length: fullStars }).map((_, i) => (
        <StarRoundedIcon key={`full-${i}`} sx={{ fontSize: 16, color: '#f59e0b' }} />
      ))}
      {hasHalf && <StarHalfRoundedIcon sx={{ fontSize: 16, color: '#f59e0b' }} />}
      {Array.from({ length: emptyStars }).map((_, i) => (
        <StarRoundedIcon key={`empty-${i}`} sx={{ fontSize: 16, color: alpha('#f59e0b', 0.25) }} />
      ))}
    </Box>
  );
};

const TestimonialCard = ({ item, border, surface, muted, isDark }) => (
  <Box
    sx={{
      height: '100%',
      p: { xs: 2, sm: 2.25 },
      borderRadius: 2.5,
      border: `1px solid ${border}`,
      bgcolor: surface,
      display: 'flex',
      flexDirection: 'column',
      background: isDark
        ? `linear-gradient(145deg, ${alpha(ACCENT, 0.1)}, transparent 55%)`
        : `linear-gradient(145deg, ${alpha(ACCENT, 0.05)}, transparent 55%)`,
    }}
  >
    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1.25 }}>
      <FormatQuoteRoundedIcon sx={{ color: alpha(ACCENT, 0.55), fontSize: 28 }} />
      <RatingStars rating={item.rating} />
    </Box>

    <Typography
      sx={{
        flex: 1,
        color: isDark ? '#e6edf3' : '#0f2a2b',
        fontSize: '0.88rem',
        lineHeight: 1.55,
        mb: 1.75,
        fontStyle: 'italic',
      }}
    >
      “{item.quote}”
    </Typography>

    <Box sx={{ display: 'flex', alignItems: 'flex-end', justifyContent: 'space-between', gap: 1, flexWrap: 'wrap' }}>
      <Box>
        <Typography sx={{ fontWeight: 800, fontSize: '0.88rem', letterSpacing: '-0.01em' }}>
          {item.name}
        </Typography>
        <Typography variant="caption" sx={{ color: muted, fontWeight: 600, display: 'block' }}>
          {item.role}
        </Typography>
      </Box>
      {item.outcome && (
        <Chip
          size="small"
          label={item.outcome}
          sx={{
            height: 22,
            fontWeight: 700,
            fontSize: '0.65rem',
            bgcolor: alpha(ACCENT, isDark ? 0.22 : 0.1),
            color: ACCENT,
          }}
        />
      )}
    </Box>
  </Box>
);

/**
 * @param {'all' | 'mentorship' | 'platform'} context
 * @param {number} [limit]
 * @param {number} [visibleCount] Max cards visible at once (desktop)
 * @param {number} [autoScrollMs] Auto-advance interval
 * @param {string} [title]
 * @param {string} [subtitle]
 * @param {string} [eyebrow]
 */
const TestimonialsSection = ({
  context = 'all',
  limit,
  visibleCount = 4,
  autoScrollMs = 5000,
  title = 'What engineers say',
  subtitle = 'Real feedback from mentorship sessions, mocks, and daily practice on CrafterHack.',
  eyebrow = 'Testimonials',
  sx = {},
}) => {
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';
  const isSm = useMediaQuery(theme.breakpoints.up('sm'));
  const isMd = useMediaQuery(theme.breakpoints.up('md'));
  const border = isDark ? '#30363d' : 'rgba(15, 60, 70, 0.1)';
  const surface = isDark ? '#161b22' : '#ffffff';
  const muted = isDark ? '#8b949e' : '#5f6b73';

  const items = filterTestimonials(context, limit);
  const perView = useMemo(() => {
    if (isMd) return Math.min(visibleCount, 4);
    if (isSm) return 2;
    return 1;
  }, [isMd, isSm, visibleCount]);

  const [startIndex, setStartIndex] = useState(0);
  const [paused, setPaused] = useState(false);
  const maxIndex = Math.max(0, items.length - perView);
  const canScroll = items.length > perView;

  useEffect(() => {
    setStartIndex(0);
  }, [perView, items.length]);

  useEffect(() => {
    if (!canScroll || paused) return undefined;

    const id = setInterval(() => {
      setStartIndex((prev) => (prev >= maxIndex ? 0 : prev + 1));
    }, autoScrollMs);

    return () => clearInterval(id);
  }, [canScroll, maxIndex, autoScrollMs, paused]);

  if (!items.length) return null;

  const cardProps = { border, surface, muted, isDark };

  return (
    <Box sx={{ mb: 4, ...sx }} data-section="testimonials">
      <Box sx={{ mb: 2.5, maxWidth: 560 }}>
        <Typography
          sx={{
            fontSize: '0.7rem',
            fontWeight: 800,
            letterSpacing: '0.08em',
            textTransform: 'uppercase',
            color: ACCENT,
            mb: 0.5,
          }}
        >
          {eyebrow}
        </Typography>
        <Typography
          sx={{
            fontWeight: 900,
            fontSize: { xs: '1.25rem', sm: '1.45rem' },
            letterSpacing: '-0.03em',
            mb: 0.5,
          }}
        >
          {title}
        </Typography>
        <Typography variant="body2" sx={{ color: muted, lineHeight: 1.55, fontSize: '0.92rem' }}>
          {subtitle}
        </Typography>
      </Box>

      <Box
        sx={{ overflow: 'hidden', width: '100%' }}
        onMouseEnter={() => setPaused(true)}
        onMouseLeave={() => setPaused(false)}
      >
        <Box
          sx={{
            display: 'flex',
            width: `${(items.length / perView) * 100}%`,
            transform: `translateX(-${(startIndex / items.length) * 100}%)`,
            transition: 'transform 0.65s ease-in-out',
          }}
        >
          {items.map((item) => (
            <Box
              key={item.id}
              sx={{
                width: `${100 / items.length}%`,
                px: 0.875,
                boxSizing: 'border-box',
              }}
            >
              <TestimonialCard item={item} {...cardProps} />
            </Box>
          ))}
        </Box>
      </Box>

      {canScroll && (
        <Box sx={{ display: 'flex', justifyContent: 'center', gap: 0.75, mt: 2 }}>
          {Array.from({ length: maxIndex + 1 }).map((_, i) => (
            <Box
              key={i}
              onClick={() => setStartIndex(i)}
              sx={{
                width: startIndex === i ? 18 : 7,
                height: 7,
                borderRadius: 999,
                bgcolor: startIndex === i ? ACCENT : alpha(ACCENT, isDark ? 0.35 : 0.25),
                cursor: 'pointer',
                transition: 'width 0.25s ease, background-color 0.25s ease',
              }}
            />
          ))}
        </Box>
      )}
    </Box>
  );
};

export default TestimonialsSection;
